import { useMemo, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { useForm } from "@tanstack/react-form"
import { TextField, DialogContent, DialogActions, Button, Dialog, DialogTitle, Stack, FormHelperText, Autocomplete } from "@mui/material"
import { track } from "helpers/analytics"
import { assertUuid } from "helpers/types/uuid"
import { validateResourceName } from "helpers/resources"
import { ValidationError, NotJsonError } from "api/errors"
import { Creators as DialogCreators } from "modules/dialog/model"
import { Creators as NotificationCreators } from "modules/notifications/model"
import { useAccountAlias } from "resources/account"
import { useFetchSigmaWorkbooks, useSaveSigmaSolution, useFetchSigmaSolutions, useUpdateSigmaSolution } from "../../hooks"
import { SOLUTIONS_EVENTS } from "../../events"
import { SigmaSolution, SigmaWorkbook } from "../../types"
import "./style.scss"

function validateSolutionName(value: string) {
  if (!validateResourceName(value)) {
    return "3-50 chars long. Alphanum, _, -, and spaces are allowed."
  }
}

function validateWorkbookId(value: string) {
  if (!value?.trim()) {
    return "Workbook ID is required"
  }
  try {
    assertUuid(value)
  } catch {
    return "Workbook ID must be a valid UUID"
  }
}

function getFetchWorkbooksErrorMessage(error: Error | null) {
  if (error instanceof ValidationError) {
    return error.errors
  }
  return "An error occurred while fetching the Sigma workbooks"
}

function mapSigmaWorkbooksToOptions(workbooks: SigmaWorkbook[]) {
  return workbooks.map((workbook) => ({
    label: workbook.name,
    value: workbook.workbookId,
    workbookUrlId: workbook.workbookUrlId,
  }))
}

interface EditSigmaSolutionProps {
  solution?: SigmaSolution | null
  onSuccess?: () => void
}

export default function EditSigmaSolution({ solution = null, onSuccess }: EditSigmaSolutionProps) {
  const [isValid, setIsValid] = useState({
    name: !!solution?.name,
    workbookId: !!solution?.workbookId,
    workbookUrlId: !!solution?.workbookUrlId,
  })
  const dispatch = useDispatch()
  const accountAlias = useAccountAlias()
  const { workbooks, isPending: isWorkbooksPending, isError: isWorkbooksError, error: workbooksError } = useFetchSigmaWorkbooks(accountAlias)
  const { solutions, isPending: isSolutionsPending } = useFetchSigmaSolutions(accountAlias)
  const sigmaWorkbookOptions = useMemo(() => mapSigmaWorkbooksToOptions(workbooks), [workbooks])
  const isEditing = Boolean(solution)
  const isSubmitDisabled = useMemo(() => {
    return !isValid.name || !isValid.workbookId || !isValid.workbookUrlId
  }, [isValid])

  function onClose() {
    dispatch(DialogCreators.close())
  }

  const saveSolution = useSaveSigmaSolution(accountAlias, () => {
    dispatch(NotificationCreators.add("info", null, "Solution created successfully"))
    track(SOLUTIONS_EVENTS.SIGMA_WORKBOOK_SOLUTION_CREATED)
    onSuccess?.()
    onClose()
  }, (error) => {
    if (error instanceof NotJsonError && error.response.status === 409) {
      dispatch(NotificationCreators.add("error", null, "A solution with this name already exists"))
    } else {
      dispatch(NotificationCreators.add("error", null, "An error occurred while creating the solution"))
    }
  })

  const updateSolution = useUpdateSigmaSolution(accountAlias, solution?.id || "", () => {
    dispatch(NotificationCreators.add("info", null, "Solution updated successfully"))
    onSuccess?.()
    onClose()
  }, () => {
    dispatch(NotificationCreators.add("error", null, "An error occurred while updating the solution"))
  })

  const defaultValues = useMemo(() => ({
    workbookId: solution?.workbookId || "",
    workbookUrlId: solution?.workbookUrlId || "",
    name: solution?.name || "",
    description: solution?.description || "",
  }), [solution])

  const form = useForm({
    defaultValues,
    onSubmit: ({ value }) => {
      if (isEditing && solution) {
        updateSolution.mutate(value)
      } else {
        saveSolution.mutate(value)
      }
    },
  })

  useEffect(() => {
    form.reset({
      ...defaultValues,
    })
  }, [form, defaultValues])

  function handleValidationOnChange(fieldName: "name" | "workbookId" | "workbookUrlId", error: string | undefined) {
    setIsValid((prev) => ({ ...prev, [fieldName]: !error }))
    return error
  }

  const selectedWorkbookOption = useMemo(() => {
    if (form.state.values.workbookId) {
      return {
        label: form.state.values.name,
        value: form.state.values.workbookId,
        workbookUrlId: form.state.values.workbookUrlId,
      }
    }
    return null
  }, [form.state.values.workbookId, form.state.values.workbookUrlId, form.state.values.name])

  return <Dialog open={true} onClose={onClose}>
    <DialogTitle>{isEditing ? "Edit Sigma Workbook Solution" : "Create Sigma Workbook Solution"}</DialogTitle>
    <form onSubmit={(e) => {
      e.preventDefault()
      e.stopPropagation()
      form.handleSubmit()
    }}
    >
      <DialogContent className="edit-sigma-solution-dialog">
        <Stack spacing={2}>
          {isWorkbooksError && <FormHelperText error>{getFetchWorkbooksErrorMessage(workbooksError)}</FormHelperText>}
          <form.Field
            name="name"
            validators={{ onChange: ({ value }) => handleValidationOnChange("name", validateSolutionName(value)) }}
          >
            {(field) => <>
              <Autocomplete
                freeSolo
                loading={isWorkbooksPending || isSolutionsPending}
                options={sigmaWorkbookOptions}
                inputValue={field.state.value}
                value={selectedWorkbookOption}
                onInputChange={(_, newValue, reason) => {
                  field.handleChange(newValue)
                  if (reason === "clear") {
                    form.setFieldValue("workbookId", "")
                    form.setFieldValue("workbookUrlId", "")
                  }
                }}
                getOptionDisabled={(option) => solutions.some((sigmaSolution) => sigmaSolution.workbookId === option.value && sigmaSolution.id !== solution?.id)}
                onChange={(_, value) => {
                  if (value && typeof value === "object") {
                    field.handleChange(value.label)
                    form.setFieldValue("workbookId", value.value)
                    form.setFieldValue("workbookUrlId", value.workbookUrlId || "")
                  } else if (typeof value === "string") {
                    field.handleChange(value)
                  } else {
                    field.handleChange("")
                  }
                }}
                renderInput={(params) => <TextField
                  {...params}
                  label="Solution Name"
                />}
              />
              {!field.state.meta.isValid && <FormHelperText error>{field.state.meta.errors.join(", ")}</FormHelperText>}
            </>}
          </form.Field>
          <form.Field
            name="workbookId"
            validators={{ onChange: ({ value }) => handleValidationOnChange("workbookId", validateWorkbookId(value)) }}
          >
            {(field) => <>
              <TextField variant="standard" id={field.name} name={field.name} label="Sigma Workbook ID" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} />
              {!field.state.meta.isValid && <FormHelperText error>{field.state.meta.errors.join(", ")}</FormHelperText>}
            </>}
          </form.Field>
          <form.Field
            name="workbookUrlId"
            validators={{ onChange: ({ value }) => handleValidationOnChange("workbookUrlId", !value?.trim() ? "Workbook URL ID is required" : undefined) }}
          >
            {(field) => <>
              <TextField variant="standard" id={field.name} name={field.name} label="Sigma Workbook URL ID" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} />
              {!field.state.meta.isValid && <FormHelperText error>{field.state.meta.errors.join(", ")}</FormHelperText>}
            </>}
          </form.Field>
          <form.Field name="description">
            {(field) => <TextField variant="standard" id={field.name} name={field.name} label="Solution Description" multiline value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} />}
          </form.Field>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary">Cancel</Button>
        <Button type="submit" color="primary" disabled={isSubmitDisabled}>{isEditing ? "Save" : "Create"}</Button>
      </DialogActions>
    </form>
  </Dialog>
}
